import { useFeatureGate as useFeatureGateBase } from '@statsig/react-bindings';
import { useAtom as useAtomBase, useAtomValue as useAtomValueBase } from 'jotai';
import { useEffect, useRef } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { Logger, logger as loggerBase } from '../../datadog/logger';
import { useIsScreenBelowBreakpoint as useIsScreenBelowBreakpointBase } from '../../hooks/useIsScreenBelowBreakpoint';
import { useTrackEvent as useTrackEventBase } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { telehealthAutoOpenIncompleteMeasuresDrawer } from '../../statsig/gates';
import {
  OutstandingMeasures,
  VisitorDrawerState,
  doesClientRequireSafetyGuardsAtom,
  visitorDrawerStateAtom,
  visitorMeasuresAtom,
} from '../state';

type Props = {
  /** Optional callback to call when the measures drawer is auto-opened. */
  onAutoOpen?: () => void;
};

export type Dependencies = {
  logger: Logger;
  useAtom: typeof useAtomBase;
  useAtomValue: typeof useAtomValueBase;
  useFeatureGate: typeof useFeatureGateBase;
  useIsScreenBelowBreakpoint: typeof useIsScreenBelowBreakpointBase;
  useParams: typeof useParamsBase;
  useTrackEvent: typeof useTrackEventBase;
};

export const DEFAULT_DEPENDENCIES: Dependencies = {
  logger: loggerBase,
  useAtom: useAtomBase,
  useAtomValue: useAtomValueBase,
  useFeatureGate: useFeatureGateBase,
  useIsScreenBelowBreakpoint: useIsScreenBelowBreakpointBase,
  useParams: useParamsBase,
  useTrackEvent: useTrackEventBase,
};

/**
 * Utility hook to auto-opens the measures drawer when a client joins the
 * waiting room, provided the feature gate is enabled and the screen is large
 * enough for the split-screen push drawer layout (>= sm breakpoint). The drawer
 * auto-opens regardless of number of outstanding measures, for two reasons:
 * - To foster a mental habit of completing measures before joining the session
 * - To avoid a jarring experience of the drawer opening several seconds *after*
 *   the client loads into the waiting room, in the case of a poor network
 *   connection or slow server response from the monolith.
 *
 * If the screen is too small, logs an info message instead of opening. Fires at
 * most once per mount.
 *
 * @param props - Primary props for the hook:
 * - onAutoOpen: Optional callback to call when the measures drawer is auto-opened.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 */
export function useAutoOpenMeasuresDrawer(
  { onAutoOpen }: Props = {},
  {
    logger,
    useAtom,
    useAtomValue,
    useFeatureGate,
    useIsScreenBelowBreakpoint,
    useParams,
    useTrackEvent,
  }: Dependencies = DEFAULT_DEPENDENCIES,
) {
  const { userViewed } = useTrackEvent();
  const { patientShortId = '' } = useParams();

  const [drawerState, setDrawerState] = useAtom(visitorDrawerStateAtom);
  const hasAutoOpenedRef = useRef(false);

  const { value: isAutoOpenEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);
  const doesClientRequireSafetyGuards = useAtomValue(doesClientRequireSafetyGuardsAtom);
  const isBelowSmBreakpoint = useIsScreenBelowBreakpoint('sm');
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);

  useEffect(
    function autoOpenMeasuresDrawerEffect() {
      if (hasAutoOpenedRef.current) return;
      if (!isAutoOpenEnabled || !patientShortId) return;

      // Do not open the drawer if the client requires safety guards.
      if (doesClientRequireSafetyGuards) {
        logSkippedAutoOpen(logger, 'client requires safety guards', patientShortId, visitorMeasures);
        return;
      }

      // Do not open the drawer if it's already open.
      if (drawerState) {
        logSkippedAutoOpen(logger, 'drawer already open', patientShortId, visitorMeasures, { drawerState });
        return;
      }

      // We'll set this ref even if we skip auto-opening due to screen size, to
      // make future effect runs short-circuit sooner.
      hasAutoOpenedRef.current = true;

      if (isBelowSmBreakpoint) {
        logSkippedAutoOpen(logger, 'screen too small for split-screen', patientShortId, visitorMeasures);
        return;
      }

      setDrawerState(VisitorDrawerState.PENDING_FORMS);
      logAutoOpen(logger, patientShortId, visitorMeasures);
      onAutoOpen?.();
      userViewed(EventTypeEnum.CLIENT_MEASURES_AUTO_OPENED, { patientShortId });
    },
    [
      isAutoOpenEnabled,
      isBelowSmBreakpoint,
      drawerState,
      doesClientRequireSafetyGuards,
      setDrawerState,
      patientShortId,
      userViewed,
      onAutoOpen,
      logger,
      visitorMeasures,
    ],
  );
}

function logSkippedAutoOpen(
  logger: Logger,
  reason: string,
  patientShortId: string,
  outstandingMeasures: OutstandingMeasures | undefined,
  otherProps: Record<string, unknown> = {},
) {
  logger.info(`Skipping auto-open of measures drawer: ${reason}`, {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
    ...otherProps,
  });
}

function logAutoOpen(logger: Logger, patientShortId: string, outstandingMeasures: OutstandingMeasures | undefined) {
  logger.info('Auto-opening measures drawer', {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
  });
}
